import { useState, useEffect } from "react";

let lowPowerLoggedOnce = false;
function computeLowPower(): boolean {
  if (typeof window === "undefined") return false;
  const conn = (navigator as any).connection;
  if (conn?.saveData === true) return true;
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * True when we should avoid video (show static hex only, no <video> tag):
 * - Data Saver / save-data is on, or
 * - User prefers reduced motion, or
 * - Page was suspended (document freeze/resume lifecycle: browser froze the page to save power, e.g. on mobile).
 */
export function useLowPowerMode(): boolean {
  const [baseLowPower, setBaseLowPower] = useState<boolean>(computeLowPower);
  const [suspended, setSuspended] = useState<boolean>(false);

  useEffect(() => {
    const update = () => setBaseLowPower(computeLowPower());
    setBaseLowPower(computeLowPower());
    const conn = (navigator as any).connection;
    conn?.addEventListener?.("change", update);

    let media: MediaQueryList | null = null;
    try {
      media = window.matchMedia("(prefers-reduced-motion: reduce)");
      media.addEventListener("change", update);
    } catch {
      // ignore
    }

    const onFreeze = () => setSuspended(true);
    const onResume = () => setSuspended(false);
    document.addEventListener("freeze", onFreeze);
    document.addEventListener("resume", onResume);

    return () => {
      conn?.removeEventListener?.("change", update);
      media?.removeEventListener?.("change", update);
      document.removeEventListener("freeze", onFreeze);
      document.removeEventListener("resume", onResume);
    };
  }, []);

  const result = baseLowPower || suspended;
  if (typeof window !== "undefined" && !lowPowerLoggedOnce) {
    console.log("[hex-video] useLowPowerMode (once)", { baseLowPower, suspended, result });
    lowPowerLoggedOnce = true;
  }
  return result;
}
